import { ResourceType } from '@cotera/api';
import { z } from 'zod';
import type { PendingChatSubmission } from '../../chat/models/pending-chat-submission';
import type {
  ChatSeedBuildInput,
  ChatSeedStrategy,
} from '../../chat-seed/types';
import { datasetChatSeedChrome } from './chrome';
import { datasetChatSeedPreSubmit } from './pre-submit';

const uuidSchema = z.string().uuid();

export const DATASET_SEED_TYPE = 'dataset';

export const NEW_DATASET_CHAT_DISPLAY_TITLE = 'New Dataset';

function parseArtifactId(searchParams: URLSearchParams): string | null {
  const raw = searchParams.get('artifactId');
  if (raw === null) {
    return null;
  }
  const parsed = uuidSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

export const datasetChatSeedStrategy: ChatSeedStrategy = {
  type: DATASET_SEED_TYPE,
  artifactResourceType: ResourceType.Dataset,
  getChatChrome: () => datasetChatSeedChrome,
  preSubmit: datasetChatSeedPreSubmit,

  buildUrlSearchParams: (input: ChatSeedBuildInput): URLSearchParams => {
    const params = new URLSearchParams();
    params.set('seed', DATASET_SEED_TYPE);
    params.set('artifactId', input.artifactId);
    if (input.isNew === true) {
      params.set('new', '1');
    }
    if (input.folderId !== undefined) {
      params.set('folderId', input.folderId);
    }
    return params;
  },

  buildPendingContext: (input: ChatSeedBuildInput) => {
    const data: Record<string, unknown> = {
      datasetId: input.artifactId,
      ...(input.isNew === true && { isNew: true }),
    };

    return {
      ...(input.isNew === true && {
        displayTitle: NEW_DATASET_CHAT_DISPLAY_TITLE,
      }),
      initialArtifacts: [
        {
          artifactType: 'dataset' as const,
          artifactId: input.artifactId,
          data,
        },
      ],
      // No tool keys are seeded: the `coco/dataset-editing` skill autoloads once
      // a dataset artifact is open in the conversation and brings the read/write/
      // edit tools with it.
      initialToolKeys: [],
    };
  },

  parseFromUrl: (
    searchParams: URLSearchParams
  ): PendingChatSubmission | null => {
    if (searchParams.get('seed') !== DATASET_SEED_TYPE) {
      return null;
    }

    const artifactId = parseArtifactId(searchParams);
    if (artifactId === null) {
      return null;
    }

    const isNew = searchParams.get('new') === '1';

    return {
      ...datasetChatSeedStrategy.buildPendingContext({ artifactId, isNew }),
      seedOnly: true,
    };
  },
};
